import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/personalized-plans/admin/fit-evolution/community-config`;

const authHeaders = () => {
  const t = localStorage.getItem('deisycfit_admin_token') || localStorage.getItem('deisycfit_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const inputCls = 'w-full px-3 py-2 rounded-lg bg-black/40 border border-white/15 text-white text-sm placeholder-white/40 focus:border-green-400 focus:outline-none';

/**
 * 2026-02 · Sección separada de administración de la comunidad Fit Evolution
 * (WhatsApp / Telegram). Cada canal se activa/desactiva y edita
 * independientemente. Antes vivía dentro de SupplementsAdminManager;
 * se movió aquí a pedido explícito para tener su propia sección con título.
 */
export default function CommunityAdminBlock() {
  const [community, setCommunity] = useState({
    whatsapp_url: '', whatsapp_enabled: false,
    telegram_url: '', telegram_enabled: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(API, { headers: authHeaders() });
        setCommunity({
          whatsapp_url: res.data?.whatsapp_url || '',
          whatsapp_enabled: !!res.data?.whatsapp_enabled,
          telegram_url: res.data?.telegram_url || '',
          telegram_enabled: !!res.data?.telegram_enabled,
        });
      } catch { /* silent */ }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(API, community, { headers: authHeaders() });
      toast.success('Comunidad actualizada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  return (
    <Card
      className="border-green-500/30"
      style={{ backgroundColor: 'rgba(34,197,94,0.04)' }}
      data-testid="community-admin-section"
    >
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          🤝 Comunidad Fit Evolution (WhatsApp / Telegram)
        </CardTitle>
        <p className="text-xs text-white/60">
          Configura los links y activa/desactiva cada canal por separado. Los canales desactivados no aparecen al usuario en el dashboard.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* WhatsApp */}
        <div className="rounded-lg p-3 bg-black/30 border border-white/10">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <p className="text-sm font-bold text-white flex items-center gap-1.5">💬 WhatsApp</p>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={community.whatsapp_enabled}
                onChange={(e) => setCommunity({ ...community, whatsapp_enabled: e.target.checked })}
                data-testid="community-whatsapp-toggle"
              />
              <span className={`text-[11px] font-bold ${community.whatsapp_enabled ? 'text-green-300' : 'text-white/60'}`}>
                {community.whatsapp_enabled ? 'ACTIVO' : 'INACTIVO'}
              </span>
            </label>
          </div>
          <input
            type="url"
            placeholder="https://chat.whatsapp.com/..."
            className={inputCls}
            value={community.whatsapp_url}
            onChange={(e) => setCommunity({ ...community, whatsapp_url: e.target.value })}
            data-testid="community-whatsapp-url"
          />
        </div>
        {/* Telegram */}
        <div className="rounded-lg p-3 bg-black/30 border border-white/10">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <p className="text-sm font-bold text-white flex items-center gap-1.5">✈️ Telegram</p>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={community.telegram_enabled}
                onChange={(e) => setCommunity({ ...community, telegram_enabled: e.target.checked })}
                data-testid="community-telegram-toggle"
              />
              <span className={`text-[11px] font-bold ${community.telegram_enabled ? 'text-green-300' : 'text-white/60'}`}>
                {community.telegram_enabled ? 'ACTIVO' : 'INACTIVO'}
              </span>
            </label>
          </div>
          <input
            type="url"
            placeholder="https://t.me/..."
            className={inputCls}
            value={community.telegram_url}
            onChange={(e) => setCommunity({ ...community, telegram_url: e.target.value })}
            data-testid="community-telegram-url"
          />
        </div>
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={save}
            disabled={saving}
            className="bg-green-600 hover:bg-green-500 text-white"
            data-testid="community-save-btn"
          >
            {saving ? 'Guardando…' : '💾 Guardar comunidad'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
